import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import { tenantService } from "@/services/api";

function getStoredUser() {
  const stored = localStorage.getItem("ezer_user");
  return stored ? JSON.parse(stored) : null;
}

export default function SaasTenantsPage() {
  const [user] = useState<any>(getStoredUser);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showNewTenant, setShowNewTenant] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    nombre: "",
    rut: "",
    direccion: "",
    comuna: "",
    region: "",
    telefono: "",
    email_institucional: "",
    admin_nombre: "",
    admin_email: "",
    admin_password: ""
  });

  const loadTenants = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await tenantService.list();
      if (Array.isArray(data)) {
        setTenants(data);
      } else {
        throw new Error("Invalid list format received from API");
      }
    } catch (err: any) {
      console.error("Error loading tenants from API:", err);
      setError("Error al conectar con la base de datos de Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.rol === "superadmin") {
      loadTenants();
    }
  }, [user]);

  if (!user || user.rol !== "superadmin") {
    return (
      <div className="text-center py-10 text-red-500">
        <i className="ri-error-warning-line text-4xl" />
        <p className="mt-2 text-lg font-bold">Acceso Denegado</p>
        <p className="text-sm">Esta sección es de uso exclusivo para el Super Administrador.</p>
      </div>
    );
  }

  const handleToggleStatus = async (tenantId: string, currentStatus: boolean) => {
    try {
      await tenantService.toggleStatus(tenantId, !currentStatus);
      await loadTenants();
    } catch (err: any) {
      alert("Error al cambiar estado en base de datos: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!formData.nombre || !formData.rut || !formData.admin_nombre || !formData.admin_email || !formData.admin_password) {
      setError("Por favor completa todos los campos obligatorios (*).");
      return;
    }

    try {
      await tenantService.create(formData);
      setShowNewTenant(false);
      setFormData({
        nombre: "",
        rut: "",
        direccion: "",
        comuna: "",
        region: "",
        telefono: "",
        email_institucional: "",
        admin_nombre: "",
        admin_email: "",
        admin_password: ""
      });
      await loadTenants();
    } catch (err: any) {
      let msg = "Error al registrar el colegio en la base de datos.";
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          msg = err.response.data.detail.map((d: any) => {
            const locStr = Array.isArray(d.loc) ? d.loc.join(".") : String(d.loc);
            return `${locStr}: ${d.msg}`;
          }).join(", ");
        } else if (typeof err.response.data.detail === "string") {
          msg = err.response.data.detail;
        }
      }
      setError(msg);
    }
  };

  const filtered = tenants.filter(
    (t) =>
      t.nombre.toLowerCase().includes(search.toLowerCase()) ||
      t.rut.toLowerCase().includes(search.toLowerCase())
  );

  const activeTenants = tenants.filter((t) => t.activo);
  const inactiveTenants = tenants.filter((t) => !t.activo);

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground-950">Administración de Colegios (Tenants)</h1>
          <p className="text-sm text-foreground-500 mt-1">Gestión global de establecimientos integrados a la plataforma Ezer Convivencia.</p>
        </div>
        <Button
          onClick={() => {
            setError("");
            setShowNewTenant(true);
          }}
          className="flex items-center justify-center gap-2 self-start sm:self-center bg-primary-500 text-white hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-add-line" />
          Nuevo Colegio
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-xs rounded-xl font-medium">
          {error}
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-building-line text-primary-600 text-lg" />
          </div>
          <div>
            <p className="text-lg font-bold text-foreground-900">{tenants.length}</p>
            <p className="text-xs text-foreground-500">Registrados</p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-line text-accent-600 text-lg" />
          </div>
          <div>
            <p className="text-lg font-bold text-foreground-900">{activeTenants.length}</p>
            <p className="text-xs text-foreground-500">Activos</p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-600 text-lg" />
          </div>
          <div>
            <p className="text-lg font-bold text-foreground-900">{inactiveTenants.length}</p>
            <p className="text-xs text-foreground-500">Suspendidos</p>
          </div>
        </Card>
        <Card className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-global-line text-secondary-600 text-lg" />
          </div>
          <div>
            <p className="text-lg font-bold text-foreground-900">SaaS</p>
            <p className="text-xs text-foreground-500">Aislamiento RLS</p>
          </div>
        </Card>
      </div>

      {/* Filter and search */}
      <div className="flex items-center justify-between">
        <Input
          placeholder="Buscar por nombre o RUT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
          className="max-w-sm"
          fullWidth={false}
        />
        {loading && <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />}
      </div>

      {/* Tenants list table */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-50/50">
                <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs">Colegio</th>
                <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs">RUT</th>
                <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs hidden sm:table-cell">Ubicación</th>
                <th className="text-center py-3 px-4 text-foreground-500 font-medium text-xs">Estado</th>
                <th className="text-left py-3 px-4 text-foreground-500 font-medium text-xs hidden md:table-cell">Fecha Creación</th>
                <th className="text-right py-3 px-4 text-foreground-500 font-medium text-xs">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-foreground-400">
                    No se encontraron establecimientos registrados.
                  </td>
                </tr>
              ) : (
                filtered.map((tenant) => (
                  <tr key={tenant.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-line text-primary-600 text-sm" />
                        </div>
                        <span className="font-semibold text-foreground-900">{tenant.nombre}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-foreground-700 font-mono">{tenant.rut}</td>
                    <td className="py-3 px-4 text-foreground-600 hidden sm:table-cell">
                      {tenant.comuna ? `${tenant.comuna}, ${tenant.region || ""}` : "No especificada"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                        tenant.activo
                          ? "bg-accent-100 text-accent-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${tenant.activo ? "bg-accent-500" : "bg-red-500"}`} />
                        {tenant.activo ? "Activo" : "Suspendido"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-foreground-500 hidden md:table-cell text-xs">
                      {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString("es-CL") : "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleStatus(tenant.id, tenant.activo)}
                          className={`transition-colors cursor-pointer p-1.5 rounded-lg hover:bg-background-100 ${
                            tenant.activo ? "text-red-500 hover:text-red-700" : "text-accent-600 hover:text-accent-800"
                          }`}
                          title={tenant.activo ? "Suspender Colegio" : "Activar Colegio"}
                        >
                          <i className={tenant.activo ? "ri-pause-circle-line text-lg" : "ri-play-circle-line text-lg"} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* New Tenant Modal */}
      {showNewTenant && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 shadow-xl" onClick={() => setShowNewTenant(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Registrar Nuevo Colegio</h2>
              <button type="button" onClick={() => setShowNewTenant(false)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            
            <form onSubmit={handleCreateTenant} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {error && (
                <div className="p-3 bg-red-50 border border-red-100 text-red-700 text-xs rounded-xl font-medium">
                  {error}
                </div>
              )}

              <h3 className="text-xs font-bold text-primary-600 uppercase tracking-wider">Datos del Establecimiento</h3>
              
              <Input
                label="Nombre del Colegio *"
                placeholder="Ej: Colegio San Agustín"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
              />
              <Input
                label="RUT Institucional *"
                placeholder="Ej: 77.777.777-7"
                value={formData.rut}
                onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                required
              />
              
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Comuna"
                  placeholder="Ej: Providencia"
                  value={formData.comuna}
                  onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                />
                <Input
                  label="Región"
                  placeholder="Ej: Metropolitana"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Teléfono"
                  placeholder="Ej: +5622345678"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                />
                <Input
                  label="Email Oficial"
                  placeholder="Ej: contacto@colegio.cl"
                  value={formData.email_institucional}
                  onChange={(e) => setFormData({ ...formData, email_institucional: e.target.value })}
                />
              </div>

              <Input
                label="Dirección Física"
                placeholder="Ej: Av. Providencia 1450"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
              />

              <hr className="border-background-200" />
              <h3 className="text-xs font-bold text-primary-600 uppercase tracking-wider">Datos del Administrador Inicial</h3>
              
              <Input
                label="Nombre Completo del Admin *"
                placeholder="Ej: Juan Pérez"
                value={formData.admin_nombre}
                onChange={(e) => setFormData({ ...formData, admin_nombre: e.target.value })}
                required
              />
              
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Email del Admin *"
                  type="email"
                  placeholder="Ej: jperez@colegio.cl"
                  value={formData.admin_email}
                  onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                  required
                />
                <Input
                  label="Contraseña del Admin *"
                  type="password"
                  placeholder="Contraseña segura"
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  required
                />
              </div>
            </form>

            <div className="flex gap-2 pt-2 border-t border-background-150">
              <Button type="button" variant="ghost" onClick={() => setShowNewTenant(false)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateTenant}
                className="flex-1 cursor-pointer bg-primary-500 hover:bg-primary-600 text-white"
              >
                Registrar Colegio
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}